import customElement from "@/wc/registry/lib/decorator/customElement/index.js";
import StekzHTMLElement from "@/wc/registry/component/base/index.js";
import type {
    IStekzBackendJSONResponse,
    IDetail,
} from "@/wc/registry/lib/service/repository/openapi/utils";
import { styles as bulmaStyles } from "@/wc/registry/lib/styles/bulma/index.js";
import { styles as fontAwesomeStyles } from "@/wc/registry/lib/styles/fontawesome/index.js";

import * as dom from "@/wc/registry/lib/dom/index.js";

export interface IFormErrorVisualizerData {
    response: Response;
    json: IStekzBackendJSONResponse;
}

// Using a typeguard (https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates)
// to specify the type that is to be used in our code.
function isDetailArray(detail: IDetail[] | string): detail is IDetail[] {
    return Array.isArray(detail);
}

// A root error is an error that can not be linked to a specific field,
// and thus should be shown in the error visualizer.
// This should be converted to make use of the generic requests that will be
// implemented by #18. Until then, json will be 'unknown'.
function getRootError(json: IStekzBackendJSONResponse): string | undefined {
    const hasDetailArray = "detail" in json;
    if (!hasDetailArray) {
        return;
    }

    let rootError = undefined;
    if (!isDetailArray(json["detail"])) {
        return;
    }

    json["detail"].find((detail) => {
        if (!("loc" in detail)) {
            return;
        }

        if (detail.loc.includes("__root__") && "msg" in detail) {
            rootError = detail.msg;
        }
    });
    return rootError;
}

// This form-error-visualizer is a component that can be nested in a c-form
// element, and is able to receive data from form requests to display
// general error messages.
// See https://my.stekz.com/Stekz/steks/MyStekz/biz/BizDevOps/products/components/#c-form-error-visualizer
// for more documentation.
const componentName = "c-form-error-visualizer";
@customElement(componentName, {
    style: fetch(
        "/wc/registry/component/form-error-visualizer/styles.css"
    ).then((res) => res.text()),
    template: fetch(
        "/wc/registry/component/form-error-visualizer/template.html"
    ).then((res) =>
        res.text().then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return doc.querySelector(`[data-component=${componentName}]`);
        })
    ),
    extraStyles: [bulmaStyles, fontAwesomeStyles],
})
export default class FormErrorVisualizer extends StekzHTMLElement {
    // Takes data passed by form, and extracts the right bits and pieces to
    // display an error message.
    set data({ json }: IFormErrorVisualizerData) {
        const hasErrorArray = "errors" in json && Array.isArray(json["errors"]);
        const rootError = getRootError(json);

        if (hasErrorArray) {
            this.nodes.notification.innerText = json["errors"].join("\n");
            dom.show(this.nodes.notification);
            // In some cases (e.g. being unauthorized), 'detail' contains one generic
            // message for some reason.
        } else if ("detail" in json && typeof json["detail"] === "string") {
            this.nodes.notification.innerText = json["detail"];
            dom.show(this.nodes.notification);
        } else if (rootError) {
            this.nodes.notification.innerText = rootError;
            dom.show(this.nodes.notification);
        } else {
            dom.hide(this.nodes.notification);
        }
    }

    reset() {
        dom.hide(this.nodes.notification);
    }

    connectedCallback() {
        dom.hide(this.nodes.notification);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-error-visualizer": FormErrorVisualizer;
    }
}
